import { Injectable, inject } from '@angular/core';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { firstValueFrom } from 'rxjs';
import {
  WorkflowRunStorageAdapter,
  type WorkflowRunStorageSnapshot,
} from '@los-workspace/workflow-runtime';
import type {
  LosWorkflowActivePointerRecord,
  LosWorkflowRunRecord,
} from './los-indexed-db.config';

@Injectable({ providedIn: 'root' })
export class NgxIndexedDbWorkflowRunStorageAdapter extends WorkflowRunStorageAdapter {
  private readonly db = inject(NgxIndexedDBService);

  override saveRun(snapshot: WorkflowRunStorageSnapshot): void {
    void this.persist(snapshot);
  }

  override async getRun(instanceId: string): Promise<WorkflowRunStorageSnapshot | null> {
    try {
      const record = await firstValueFrom(
        this.db.getByKey<LosWorkflowRunRecord>('workflowRuns', instanceId),
      );
      if (!record?.snapshotJson) {
        return null;
      }
      return JSON.parse(record.snapshotJson) as WorkflowRunStorageSnapshot;
    } catch {
      return null;
    }
  }

  override async getActiveInstanceId(workflowKey: string): Promise<string | null> {
    try {
      const record = await firstValueFrom(
        this.db.getByKey<LosWorkflowActivePointerRecord>(
          'workflowActivePointers',
          workflowKey.toUpperCase(),
        ),
      );
      return record?.instanceId ?? null;
    } catch {
      return null;
    }
  }

  /** Remove all persisted runs and the active pointer for a workflow key. */
  async clearWorkflow(workflowKey: string): Promise<void> {
    const normalizedKey = workflowKey.toUpperCase();
    try {
      const instanceIds = await this.listInstanceIdsForWorkflow(normalizedKey);
      for (const instanceId of instanceIds) {
        await firstValueFrom(this.db.deleteByKey('workflowRuns', instanceId)).catch(
          () => undefined,
        );
      }
      await firstValueFrom(
        this.db.deleteByKey('workflowActivePointers', normalizedKey),
      ).catch(() => undefined);
    } catch {
      // ignore
    }
  }

  override async listInstanceIdsForWorkflow(workflowKey: string): Promise<string[]> {
    const normalizedKey = workflowKey.toUpperCase();
    try {
      const records = await firstValueFrom(
        this.db.getAllByIndex<LosWorkflowRunRecord>(
          'workflowRuns',
          'workflowKey',
          normalizedKey,
        ),
      );
      if (records.length) {
        return records.map((record) => record.instanceId);
      }
    } catch {
      // fall through to full scan
    }

    try {
      const records = await firstValueFrom(this.db.getAll<LosWorkflowRunRecord>('workflowRuns'));
      return records
        .filter((record) => record.workflowKey?.toUpperCase() === normalizedKey)
        .map((record) => record.instanceId);
    } catch {
      return [];
    }
  }

  private async persist(snapshot: WorkflowRunStorageSnapshot): Promise<void> {
    const workflowKey = snapshot.workflowKey.toUpperCase();
    const runRecord: LosWorkflowRunRecord = {
      instanceId: snapshot.instanceId,
      workflowKey,
      snapshotJson: JSON.stringify(snapshot),
    };
    const pointerRecord: LosWorkflowActivePointerRecord = {
      workflowKey,
      instanceId: snapshot.instanceId,
    };

    try {
      const existingRun = await firstValueFrom(
        this.db.getByKey<LosWorkflowRunRecord>('workflowRuns', snapshot.instanceId),
      ).catch(() => null);

      if (existingRun) {
        await firstValueFrom(this.db.update('workflowRuns', runRecord));
      } else {
        await firstValueFrom(this.db.add('workflowRuns', runRecord));
      }

      const existingPointer = await firstValueFrom(
        this.db.getByKey<LosWorkflowActivePointerRecord>(
          'workflowActivePointers',
          workflowKey,
        ),
      ).catch(() => null);

      if (existingPointer) {
        await firstValueFrom(this.db.update('workflowActivePointers', pointerRecord));
      } else {
        await firstValueFrom(this.db.add('workflowActivePointers', pointerRecord));
      }
    } catch {
      // ignore quota / private mode
    }
  }
}
